<template>
  <div>
    <title-bar :title-stack="['SECAPI', 'Acessos', 'Pessoas']" />
    <CardComponent>
      <card-header
        :title="'Listar Acessos de Pessoas'"
        :route="'/acessos/cadastrar/pessoa'"
      />
      <ModalDelete
        :isActive="modalDelectActive"
        :item="acessoSelecionado"
        @confirm="(id) => deleteAcesso(id)"
        @cancel="closeModal"
      />
      <ModalRecusarAcesso
        :isActive="modalRecusarActive"
        :item="acessoSelecionado"
        @confirm="(payload) => recusarAcesso(payload)"
        @cancel="closeModal"
      />
      <FilterAcessoPessoa @filtrar="handleFilter" />
      <b-table
        :data="acessos"
        striped
        hoverable
        default-sort="nome"
        narrowed
        class="is-fullwidth tabela"
      >
        <b-table-column field="nome" label="Nome">
          <template slot-scope="props">
            {{ props.row.pessoa?.nome ?? "N/A" }}
          </template>
        </b-table-column>

        <b-table-column field="documento" label="Documento">
          <template slot-scope="props">
            {{
              props.row.pessoa?.cpf
                ? $util.formatarCPF(props.row.pessoa.cpf)
                : props.row.pessoa?.passaporte
            }}
          </template>
        </b-table-column>

        <b-table-column field="tipo" label="Tipo">
          <template slot-scope="props">
            {{ props.row.tipo }}
          </template>
        </b-table-column>

        <b-table-column field="empresa" label="Empresa">
          <template slot-scope="props">
            {{
              props.row.empresa
                ? props.row.empresa.razaoSocial
                : "Empresa não cadastrada"
            }}
          </template>
        </b-table-column>

        <b-table-column field="status" label="Status">
          <template slot-scope="props">
            <StatusTag :status="props.row.status" />
          </template>
        </b-table-column>

        <b-table-column field="ações" label="Ações">
          <template slot-scope="props">
            <ActionButtons
              :item="props.row"
              :current-route="currentRoute"
              :toConfig="{
                name: 'EditarAcessoPessoa',
                params: { id: props.row.id },
              }"
              :toApprove="true"
              :toRefuse="true"
              :viewMotivoRecusa="
                props.row.motivoRecusa && props.row.status === 'RECUSADO'
                  ? props.row.motivoRecusa
                  : null
              "
              @viewMotivoRecusa="(motivo) => abrirModalMotivoRecusa(motivo)"
              @delete="(p) => openModalDelete(p)"
              @approve="(id) => aprovarAcesso(id)"
              @refuse="(payload) => openModalRecusar(payload)"
            />
          </template>
        </b-table-column>
      </b-table>
      <!-- Paginação -->
      <pagination-section
        :current-page="pageable.current"
        :total-items="totalElements"
        :per-page="15"
        :current-query="$route.query"
        @page-change="handlePageChange"
      />
    </CardComponent>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import AcessosService from "@/services/AcessosService";
import TitleBar from "@/components/TitleBar.vue";
import CardHeader from "@/components/Cards/CardHeader.vue";
import CardComponent from "@/components/Cards/CardComponent.vue";
import PaginationSection from "@/components/PaginationSection.vue";
import StatusTag from "@/components/StatusTag.vue";
import ActionButtons from "@/components/ActionButtons.vue";
import ModalDelete from "@/components/Modals/ModalDelete.vue";
import ModalRecusarAcesso from "@/components/Modals/ModalRecusarAcesso.vue";
import FilterAcessoPessoa from "@/components/Filters/FilterAcessoPessoa.vue";

export default defineComponent({
  name: "ViewListAcesso",
  components: {
    TitleBar,
    CardHeader,
    CardComponent,
    PaginationSection,
    StatusTag,
    ActionButtons,
    ModalDelete,
    ModalRecusarAcesso,
    FilterAcessoPessoa,
  },
  data() {
    return {
      filter: {
        nome: null,
        status: null,
        tipo: null,
        nomeEmpresa: null,
      },
      modalDelectActive: false,
      modalRecusarActive: false,
      acessoSelecionado: null,
      acessos: [],
      pageable: {
        current: 1,
        size: 0,
      },
      totalElements: 0,
    };
  },
  computed: {
    currentRoute() {
      return this.$route;
    },
  },
  mounted() {
    this.loadAcessos();
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.loadAcessos();
      },
    },
  },
  methods: {
    handlePageChange(newPage) {
      this.pageable.current = newPage;
      this.$router.push({
        query: {
          ...this.$route.query,
          page: newPage,
        },
      });
    },
    handleFilter(filter) {
      const newQuery = {
        ...this.currentRoute.query,
        ...filter,
        page: 0,
      };
      Object.keys(newQuery).forEach((key) => {
        if (!newQuery[key]) delete newQuery[key];
      });

      this.$router.push({ query: newQuery });
    },
    async loadAcessos() {
      try {
        const page = Number(this.$route.query.page) || 0;
        this.pageable.current = page;

        const response = await AcessosService.getAcessos(
          page - 1,
          this.$util.formatQueryUrl(this.currentRoute.query)
        );

        this.acessos = response.data.content;
        this.totalElements = response.data.totalElements;
        this.pageable.size = response.data.size; // Correção do tamanho da página
      } catch (error) {
        console.error(error);
      }
    },
    async aprovarAcesso(id) {
      try {
        await AcessosService.setStatusAcessoPessoa(id, "ATIVO");
        this.$buefy.toast.open({
          message: `Acesso Aprovado com sucesso!`,
          position: "is-bottom",
          type: "is-info",
        });
        this.loadAcessos();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response.data.message,
          position: "is-bottom",
          type: "is-danger",
        });
      }
    },
    async recusarAcesso(payload) {
      try {
        const { id, motivoRecusa } = payload;
        await AcessosService.setRecusarAcessoPessoa(id, motivoRecusa);
        this.$buefy.toast.open({
          message: `Acesso Recusado com sucesso!`,
          position: "is-bottom",
          type: "is-info",
        });
        this.loadAcessos();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response.data.message,
          position: "is-bottom",
          type: "is-danger",
        });
      }
      this.modalRecusarActive = false;
      this.acessoSelecionado = null;
    },
    async deleteAcesso(id) {
      try {
        console.log("deleting id:", id);
        await AcessosService.setStatusAcessoPessoaInativo(id);
        this.loadAcessos();
        this.closeModal();
        this.$buefy.toast.open({
          message: `Acesso inativado com sucesso!`,
          position: "is-bottom",
          type: "is-info",
        });
      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          message: error.response.data.message ?? `Ocorreu um erro!`,
          position: "is-bottom",
          type: "is-danger",
        });
      }
    },
    openModalDelete(item) {
      this.modalDelectActive = true;
      this.acessoSelecionado = item;
    },
    openModalRecusar(item) {
      this.modalRecusarActive = true;
      this.acessoSelecionado = item;
    },
    abrirModalMotivoRecusa(motivo) {
      this.$buefy.dialog.alert({
        title: "Motivo da Recusa",
        message: motivo,
        type: "is-danger",
        hasIcon: true,
        icon: "times-circle",
        iconPack: "fa",
        ariaRole: "alertdialog",
        ariaModal: true,
      });
    },
    closeModal() {
      this.modalDelectActive = false;
      this.modalRecusarActive = false;
      this.acessoSelecionado = null;
    },
  },
});
</script>

<style>
.tabela {
  margin-bottom: 8px;
  width: 100%;
  min-height: 500px;
}
</style>
